// Role Management Blueprint v2.3 · Phase 3 — Role Detail (registry-driven).
//
// Route: /admin/roles/:code
//
// The role's saved ``permissions[]`` array is resolved back into
// Business Capabilities from the Business Capability Registry and
// grouped by Module → Workflow → Function. Every rendered string
// is registry-owned; no permission codes are displayed.
//
// Data sources:
//   GET /admin/role/:code                (existing, unchanged)
//   GET /admin/role/:code/usage          (existing, unchanged)
//   GET /admin/access/taxonomy           (Phase 1)
//   GET /admin/access/capability-codes-map  (Phase 2 addition)
//
// Guardrails observed (Phase 3 scope):
//   * Existing edit / delete / matrix / audit navigation buttons intact.
//   * Users-holding-role panel unchanged in behaviour.
//   * Unmapped codes are surfaced as a "Legacy access preserved" fallback
//     panel — never silently dropped.
//   * Deprecated capabilities render with a muted "Deprecated" marker.
//   * No PDE / permission-code / advanced-details concepts.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, PencilSimple, Trash, ShieldCheck, Users as UsersIcon,
    Warning, ChartBar, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "../../contexts/AuthContext";

const RISK_STYLES = {
    Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Medium: "bg-slate-100 text-slate-700 border-slate-200",
    High: "bg-orange-100 text-orange-700 border-orange-200",
    Critical: "bg-rose-100 text-rose-700 border-rose-200",
};

const TYPE_STYLES = {
    View: "bg-slate-50 text-slate-700 border-slate-200",
    Maintain: "bg-sky-50 text-sky-700 border-sky-200",
    Transact: "bg-blue-50 text-blue-700 border-blue-200",
    Approve: "bg-violet-50 text-violet-700 border-violet-200",
    Process: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Administration: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    Reporting: "bg-teal-50 text-teal-700 border-teal-200",
    Governance: "bg-amber-50 text-amber-800 border-amber-200",
    Onboarding: "bg-cyan-50 text-cyan-700 border-cyan-200",
    Automation: "bg-pink-50 text-pink-700 border-pink-200",
};

export default function RoleDetail() {
    const { code } = useParams();
    const { user } = useAuth();
    const canWrite = user?.role === "super_admin";

    const [role, setRole] = useState(null);
    const [holders, setHolders] = useState([]);
    const [taxonomy, setTaxonomy] = useState(null);
    const [codesByCapability, setCodesByCapability] = useState({});
    const [scopeLabels, setScopeLabels] = useState({});
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [roleResp, usageResp, taxResp, codesResp] = await Promise.all([
                api.get(`/admin/role/${code}`),
                api.get(`/admin/role/${code}/usage`),
                api.get("/admin/access/taxonomy"),
                api.get("/admin/access/capability-codes-map"),
            ]);
            setRole(roleResp.data);
            setHolders(usageResp.data.items || []);
            setTaxonomy(taxResp.data);
            setCodesByCapability(codesResp.data.codes_by_capability || {});
            const scopes = {};
            for (const s of taxResp.data.data_scopes || []) scopes[s.identifier] = s.label;
            setScopeLabels(scopes);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [code]);

    useEffect(() => { reload(); }, [reload]);

    // ── Flatten taxonomy into a capability lookup + hierarchy trace ──
    const { flatCapabilities, capabilityById } = useMemo(() => {
        if (!taxonomy) return { flatCapabilities: [], capabilityById: {} };
        const flat = [];
        for (const m of taxonomy.modules || []) {
            for (const c of m.capabilities || []) {
                flat.push({ ...c, _module: m, _workflow: null, _function: null });
            }
            for (const w of m.workflows || []) {
                for (const c of w.capabilities || []) {
                    flat.push({ ...c, _module: m, _workflow: w, _function: null });
                }
                for (const f of w.functions || []) {
                    for (const c of f.capabilities || []) {
                        flat.push({ ...c, _module: m, _workflow: w, _function: f });
                    }
                }
            }
        }
        const byId = {};
        for (const c of flat) byId[c.capability_identifier] = c;
        return { flatCapabilities: flat, capabilityById: byId };
    }, [taxonomy]);

    // ── Reconstruct which capabilities this role actually carries ──
    // A capability is "granted" iff every one of its save codes is in
    // role.permissions. Any code not accounted for by any granted
    // capability is preserved as a "legacy" grant.
    const { grantedByModule, moduleOrder, legacyCodes } = useMemo(() => {
        if (!role || !taxonomy) return { grantedByModule: {}, moduleOrder: [], legacyCodes: [] };
        const rolePerms = new Set(role.permissions || []);
        const usedCodes = new Set();
        const granted = [];
        for (const cap of flatCapabilities) {
            const codes = codesByCapability[cap.capability_identifier] || [];
            if (!codes.length) continue; // class-C: never stored on roles
            if (codes.every((c) => rolePerms.has(c))) {
                granted.push(cap);
                codes.forEach((c) => usedCodes.add(c));
            }
        }
        // Preserve ordering as it appears in the taxonomy sort.
        const byMod = {};
        const modOrder = [];
        for (const m of taxonomy.modules || []) {
            const modCaps = granted.filter((c) => c._module?.identifier === m.identifier);
            if (modCaps.length) {
                byMod[m.identifier] = { module: m, capabilities: modCaps };
                modOrder.push(m.identifier);
            }
        }
        const legacy = [...rolePerms].filter((c) => !usedCodes.has(c)).sort();
        return { grantedByModule: byMod, moduleOrder: modOrder, legacyCodes: legacy };
    }, [role, taxonomy, flatCapabilities, codesByCapability]);

    const totalCapabilities = useMemo(() => {
        return Object.values(grantedByModule).reduce((acc, m) => acc + m.capabilities.length, 0);
    }, [grantedByModule]);

    const dangerCount = useMemo(() => {
        let n = 0;
        for (const m of Object.values(grantedByModule)) {
            for (const c of m.capabilities) if (c.flags?.danger) n += 1;
        }
        return n;
    }, [grantedByModule]);

    const deprecatedCount = useMemo(() => {
        let n = 0;
        for (const m of Object.values(grantedByModule)) {
            for (const c of m.capabilities) if (c.deprecation?.status === "deprecated") n += 1;
        }
        return n;
    }, [grantedByModule]);

    // UAT RBAC-UX-03 (2026-07-14) — Business "Can / Cannot" summary.
    //
    // Semantics:
    //   * "Can" = every business capability actually granted by this role,
    //             grouped by module in registry sort order.
    //   * "Cannot" = capabilities the role does NOT hold *within the same
    //                modules it already touches* — this is the useful
    //                comparison surface. We skip pure "View" and "Reporting"
    //                type capabilities from the Cannot list (visibility is a
    //                separate concern from operational absence) and skip
    //                deprecated + class-C caps (class-C never appears in
    //                permissions[] by definition).
    //
    // Rendered inline (short, module-grouped) so the operator can scan the
    // role's business footprint at a glance without decoding capability
    // hierarchies. Uses existing registry metadata only.
    const { canSummary, cannotSummary } = useMemo(() => {
        if (!taxonomy) return { canSummary: [], cannotSummary: [] };
        const rolePerms = new Set(role?.permissions || []);
        const touchedModuleIds = new Set(moduleOrder);
        const can = [];
        const cannot = [];
        const cannotSkipTypes = new Set(["View", "Reporting"]);
        for (const m of taxonomy.modules || []) {
            const isTouched = touchedModuleIds.has(m.identifier);
            const collect = (cap) => {
                const codes = codesByCapability[cap.capability_identifier] || [];
                if (!codes.length) return; // class-C ignored
                const granted = codes.every((c) => rolePerms.has(c));
                if (granted) {
                    can.push({ module: m, cap });
                    return;
                }
                if (!isTouched) return;
                if (cap.deprecation?.status === "deprecated") return;
                if (cannotSkipTypes.has(cap.capability_type)) return;
                cannot.push({ module: m, cap });
            };
            for (const c of m.capabilities || []) collect(c);
            for (const w of m.workflows || []) {
                for (const c of w.capabilities || []) collect(c);
                for (const f of w.functions || []) {
                    for (const c of f.capabilities || []) collect(c);
                }
            }
        }
        return { canSummary: can, cannotSummary: cannot };
    }, [taxonomy, role, moduleOrder, codesByCapability]);

    const doDelete = async () => {
        if (!confirm(`Delete role '${role.name}'?\nThis cannot be undone.`)) return;
        try {
            await api.delete(`/admin/role/${code}`);
            toast.success(`Role '${code}' deleted`);
            window.location.assign("/admin/roles");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === "object" && detail?.users_holding_role) {
                toast.error(`Cannot delete: ${detail.users_holding_role} user(s) still hold this role.`);
            } else {
                toast.error(formatApiError(err));
            }
        }
    };

    if (loading) {
        return <div className="p-6 text-sm text-slate-500" data-testid="role-detail-loading">Loading...</div>;
    }
    if (!role) {
        return <div className="p-6 text-sm text-rose-500" data-testid="role-detail-not-found">Role &apos;{code}&apos; not found.</div>;
    }

    return (
        <div className="space-y-6" data-testid={`role-detail-${code}`}>
            <PageHeader
                title={role.name}
                subtitle={
                    <span className="flex items-center gap-2">
                        <code className="text-xs px-1.5 py-0.5 bg-slate-100 rounded font-mono">{role.code}</code>
                        {role.is_system ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                <ShieldCheck className="w-3 h-3 mr-1" /> System role
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                Custom role
                            </span>
                        )}
                        <StatusBadge status={role.is_active ? "active" : "inactive"} />
                    </span>
                }
                breadcrumbs={[
                    { label: "Administration" },
                    { label: "Roles", to: "/admin/roles" },
                    { label: role.name },
                ]}
                actions={
                    <div className="flex items-center gap-2">
                        <Link to="/admin/roles">
                            <Button variant="outline" data-testid="back-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Back
                            </Button>
                        </Link>
                        {/* UAT RBAC-UX-04 (2026-07-14) — Permission Matrix is an
                            engineering diagnostic surface (raw permission codes).
                            Gate its entry point behind Primary Super Admin
                            ("Internal Engineering Mode") so normal admins are
                            not led into a code-first view. Route itself remains
                            reachable via direct URL for PSA bookmarks. */}
                        {user?.is_primary_admin === true && (
                            <Link to="/admin/roles/matrix">
                                <Button variant="outline" data-testid="open-matrix-btn">
                                    <ChartBar className="w-4 h-4 mr-2" /> Matrix
                                </Button>
                            </Link>
                        )}
                        {canWrite && role.code !== "super_admin" && (
                            <Link to={`/admin/roles/${code}/edit`}>
                                <Button data-testid="edit-role-btn">
                                    <PencilSimple className="w-4 h-4 mr-2" /> Edit
                                </Button>
                            </Link>
                        )}
                        {canWrite && !role.is_system && (
                            <Button variant="destructive" onClick={doDelete} data-testid="delete-role-btn">
                                <Trash className="w-4 h-4 mr-2" /> Delete
                            </Button>
                        )}
                    </div>
                }
            />

            {role.code === "super_admin" && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-3 text-sm flex items-start gap-2" data-testid="super-admin-banner">
                    <Warning className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium">This role is locked.</div>
                        <div className="text-xs">Primary administrative access is preserved as-is. Edit and delete requests are refused server-side (HTTP 409) and its capability set is auto-reconciled on every boot.</div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-5">

                    {/* UAT RBAC-UX-03 (2026-07-14) — Business summary "Can / Cannot".
                        Placed at the top of the main column so the operator
                        reads the role's business footprint in seconds without
                        parsing the capability tree. Uses registry metadata
                        only — no new data contract. */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="role-detail-can-cannot">
                        <h3 className="font-semibold text-slate-900 mb-3">Business summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <CanCannotColumn
                                tone="emerald"
                                title="This role can"
                                items={canSummary}
                                empty="This role holds no business capabilities."
                                testId="role-detail-can-list"
                            />
                            <CanCannotColumn
                                tone="rose"
                                title="This role cannot"
                                items={cannotSummary}
                                empty={moduleOrder.length === 0
                                    ? "No modules are in scope, so nothing to compare."
                                    : "Within the modules this role touches, no further operational capabilities are missing."}
                                testId="role-detail-cannot-list"
                            />
                        </div>
                        {cannotSummary.length > 0 && (
                            <p className="text-[11px] text-slate-500 mt-3 italic">
                                &quot;Cannot&quot; is scoped to the {moduleOrder.length} module{moduleOrder.length === 1 ? "" : "s"} this role touches.
                                Read-only visibility (View / Reporting) is intentionally omitted here — visibility gaps are a separate concern.
                            </p>
                        )}
                    </div>

                    {/* UAT RBAC-UX-02 (2026-07-14) — Access hierarchy elevated
                        as the primary content of the page: Module → Workflow
                        → Function → Capability. Rendered with stronger visual
                        weight (indigo header, larger heading, more padding). */}
                    <div className="bg-white border-2 border-indigo-200 rounded-lg shadow-sm" data-testid="role-detail-capabilities">
                        <div className="flex items-center justify-between px-5 py-4 bg-indigo-50 border-b border-indigo-200 rounded-t-lg">
                            <div>
                                <h3 className="text-base font-semibold text-indigo-900">Access hierarchy</h3>
                                <p className="text-[11px] text-indigo-700 mt-0.5">Module → Workflow → Function → Capability</p>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-mono font-semibold text-indigo-900">{totalCapabilities}</div>
                                <div className="text-[10px] uppercase tracking-wider text-indigo-700">capabilities</div>
                            </div>
                        </div>
                        <div className="px-5 py-5">
                        {totalCapabilities === 0 ? (
                            <p className="text-sm text-slate-400 italic" data-testid="role-detail-no-capabilities">
                                This role grants no business capabilities.
                            </p>
                        ) : (
                            <div className="space-y-5">
                                {moduleOrder.map((mid) => {
                                    const { module: mod, capabilities: caps } = grantedByModule[mid];
                                    // Group inside module by workflow → function for readable structure.
                                    const byWorkflow = {};
                                    const moduleScope = [];
                                    for (const c of caps) {
                                        if (!c._workflow) {
                                            moduleScope.push(c);
                                            continue;
                                        }
                                        const wid = c._workflow.identifier;
                                        (byWorkflow[wid] ||= { workflow: c._workflow, workflowScope: [], byFunction: {}, fnOrder: [] });
                                        if (!c._function) {
                                            byWorkflow[wid].workflowScope.push(c);
                                        } else {
                                            const fid = c._function.identifier;
                                            if (!byWorkflow[wid].byFunction[fid]) {
                                                byWorkflow[wid].byFunction[fid] = { fn: c._function, capabilities: [] };
                                                byWorkflow[wid].fnOrder.push(fid);
                                            }
                                            byWorkflow[wid].byFunction[fid].capabilities.push(c);
                                        }
                                    }
                                    return (
                                        <div key={mod.identifier} className="border-l-4 border-indigo-200 pl-4" data-testid={`role-detail-module-${mod.identifier}`}>
                                            <div className="text-xs uppercase tracking-wide font-semibold text-slate-700 mb-2">
                                                {mod.label}
                                                <span className="ml-2 text-[10px] font-mono text-slate-400 normal-case font-normal">
                                                    {caps.length} capabilities
                                                </span>
                                            </div>
                                            {moduleScope.map((c) => (
                                                <CapabilityCard key={c.capability_identifier} cap={c} scopeLabels={scopeLabels} indent={0} />
                                            ))}
                                            {Object.values(byWorkflow).map(({ workflow, workflowScope, byFunction, fnOrder }) => (
                                                <div key={workflow.identifier} className="mt-2" data-testid={`role-detail-workflow-${workflow.identifier}`}>
                                                    <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                                                        {workflow.label}
                                                    </div>
                                                    {workflowScope.map((c) => (
                                                        <CapabilityCard key={c.capability_identifier} cap={c} scopeLabels={scopeLabels} indent={1} />
                                                    ))}
                                                    {fnOrder.map((fid) => {
                                                        const { fn, capabilities } = byFunction[fid];
                                                        return (
                                                            <div key={fid} className="ml-3 mt-1" data-testid={`role-detail-function-${fid}`}>
                                                                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">
                                                                    {fn.label}
                                                                </div>
                                                                {capabilities.map((c) => (
                                                                    <CapabilityCard key={c.capability_identifier} cap={c} scopeLabels={scopeLabels} indent={2} />
                                                                ))}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        </div>
                    </div>

                    {/* Compact stats strip (moved below the access hierarchy so the
                        tree owns the operator's attention first). */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="role-detail-summary">
                        <div className="flex flex-wrap items-center gap-3">
                            <StatChip label="Capabilities" value={totalCapabilities} tone="indigo" />
                            <StatChip label="Modules touched" value={moduleOrder.length} tone="slate" />
                            <StatChip label="Sensitive" value={dangerCount} tone={dangerCount ? "rose" : "muted"} />
                            {deprecatedCount > 0 && (
                                <StatChip label="Deprecated" value={deprecatedCount} tone="amber" />
                            )}
                            {legacyCodes.length > 0 && (
                                <StatChip label="Legacy grants" value={legacyCodes.length} tone="muted" />
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5">
                        <h3 className="font-semibold text-slate-900 mb-1">Description</h3>
                        <p className="text-sm text-slate-700" data-testid="role-description">
                            {role.description || <span className="text-slate-400 italic">(no description)</span>}
                        </p>
                    </div>

                    {/* Legacy grants (fallback for unmapped codes) */}
                    {legacyCodes.length > 0 && (
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-5" data-testid="role-detail-legacy">
                            <div className="flex items-start gap-2 mb-2">
                                <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-slate-500" />
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900">Legacy access preserved</h4>
                                    <p className="text-xs text-slate-600 mt-0.5">
                                        This role carries {legacyCodes.length} historical access grant{legacyCodes.length === 1 ? "" : "s"} that predate the current capability registry. They remain in effect and will be preserved through future edits — no business meaning has been catalogued for them yet.
                                    </p>
                                </div>
                            </div>
                            <div className="text-[10px] text-slate-500 italic ml-7">
                                Contact engineering to map these into the registry — until then they render as an uncategorised access set.
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: users holding this role */}
                <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="role-detail-holders">
                    <div className="flex items-center gap-2 mb-3">
                        <UsersIcon className="w-5 h-5 text-slate-600" />
                        <h3 className="font-semibold text-slate-900">
                            Users holding this role
                            <span className="ml-2 text-xs font-mono text-slate-500">{holders.length}</span>
                        </h3>
                    </div>
                    {holders.length === 0 ? (
                        <p className="text-sm text-slate-400 italic" data-testid="no-holders">No active users hold this role.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 -mx-5">
                            {holders.map((u) => (
                                <li key={u.id} className="px-5 py-2.5 hover:bg-slate-50" data-testid={`holder-${u.id}`}>
                                    <div className="font-medium text-sm text-slate-900">{u.name}</div>
                                    <div className="text-xs text-slate-500">{u.email}</div>
                                    {u.linked_employee_id && (
                                        <div className="text-xs text-emerald-600 font-mono mt-0.5">{u.linked_employee_id}</div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {!role.is_system && holders.length > 0 && (
                        <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3">
                            <Warning className="w-3 h-3 inline mr-1" />
                            Deletion is blocked while {holders.length} user{holders.length === 1 ? "" : "s"} hold this role. Reassign them first.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function CapabilityCard({ cap, scopeLabels, indent }) {
    const isDeprecated = cap.deprecation?.status === "deprecated";
    return (
        <div
            className={`py-1.5 ${isDeprecated ? "opacity-70" : ""}`}
            style={{ paddingLeft: `${indent * 12}px` }}
            data-testid={`role-detail-cap-${cap.capability_identifier}`}
        >
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-900" data-testid={`role-detail-cap-name-${cap.capability_identifier}`}>
                    {cap.business_name}
                </span>
                {cap.capability_type && (
                    <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-medium border ${TYPE_STYLES[cap.capability_type] || TYPE_STYLES.View}`}>
                        {cap.capability_type}
                    </span>
                )}
                {cap.risk_level && (
                    <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-medium border ${RISK_STYLES[cap.risk_level] || RISK_STYLES.Medium}`}>
                        {cap.risk_level}
                    </span>
                )}
                {cap.default_data_scope && (
                    <span
                        className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium border bg-white text-slate-600 border-slate-200"
                        title={cap.available_data_scopes && cap.available_data_scopes.length > 1
                            ? `Possible scopes: ${cap.available_data_scopes.map((s) => scopeLabels[s] || s).join(", ")}`
                            : undefined}
                    >
                        {scopeLabels[cap.default_data_scope] || cap.default_data_scope}
                    </span>
                )}
                {cap.flags?.danger && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-rose-50 text-rose-700 border border-rose-100">
                        <Warning className="w-2.5 h-2.5" /> Sensitive
                    </span>
                )}
                {cap.flags?.dual_signoff && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-amber-50 text-amber-800 border border-amber-100">4-eye</span>
                )}
                {cap.flags?.psa_only && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-100">Primary Admin only</span>
                )}
                {isDeprecated && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-medium bg-slate-200 text-slate-700 border border-slate-300">
                        Deprecated
                    </span>
                )}
            </div>
            {cap.business_description && (
                <div className="text-[11px] text-slate-500 mt-0.5">{cap.business_description}</div>
            )}
        </div>
    );
}

function StatChip({ label, value, tone }) {
    const tones = {
        indigo: "bg-indigo-50 text-indigo-800 border-indigo-100",
        slate: "bg-slate-50 text-slate-700 border-slate-200",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        muted: "bg-slate-50 text-slate-500 border-slate-200",
    };
    return (
        <div className={`inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-md border ${tones[tone] || tones.slate}`}>
            <span className="text-lg font-mono font-semibold">{value}</span>
            <span className="text-[11px] uppercase tracking-wider">{label}</span>
        </div>
    );
}


// UAT RBAC-UX-03 (2026-07-14) — Business "Can / Cannot" column renderer.
// Purely presentational; consumes items produced by RoleDetail's memoised
// canSummary / cannotSummary arrays.
function CanCannotColumn({ tone, title, items, empty, testId }) {
    const toneStyles = tone === "emerald"
        ? { title: "text-emerald-700", chip: "bg-emerald-50 text-emerald-800 border-emerald-100", dot: "bg-emerald-500" }
        : { title: "text-rose-700", chip: "bg-rose-50 text-rose-800 border-rose-100", dot: "bg-rose-500" };
    const VISIBLE = 8;
    const visible = items.slice(0, VISIBLE);
    const overflow = items.length - visible.length;

    // Group by module for readable bullet list.
    const byModule = new Map();
    for (const it of visible) {
        if (!byModule.has(it.module.identifier)) byModule.set(it.module.identifier, { label: it.module.label, caps: [] });
        byModule.get(it.module.identifier).caps.push(it.cap);
    }

    return (
        <div data-testid={testId}>
            <div className={`text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 ${toneStyles.title}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${toneStyles.dot}`}></span>
                {title}
                <span className="ml-auto text-slate-400 font-mono">{items.length}</span>
            </div>
            {items.length === 0 ? (
                <p className="text-xs text-slate-400 italic">{empty}</p>
            ) : (
                <div className="space-y-2">
                    {[...byModule.values()].map((m) => (
                        <div key={m.label}>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{m.label}</div>
                            <ul className="space-y-0.5">
                                {m.caps.map((c) => (
                                    <li
                                        key={c.capability_identifier}
                                        className={`text-xs px-2 py-1 rounded border ${toneStyles.chip}`}
                                        data-testid={`${testId}-item-${c.capability_identifier}`}
                                        title={c.business_description || undefined}
                                    >
                                        {c.business_name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {overflow > 0 && (
                        <div className="text-[11px] text-slate-500 italic mt-1" data-testid={`${testId}-overflow`}>
                            + {overflow} more
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
